import { AppIcon, AppText } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppBaseScreen from "@toothfairy/shared-ui/AppBaseScreen";
import AppDropDown from "@toothfairy/shared-ui/AppDropDown";
import AppList from "@toothfairy/shared-ui/AppList";
import AppTable from "@toothfairy/shared-ui/AppTable";
import AppPlaceholderView from "@toothfairy/shared-ui/AppPlaceholderView";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Platform, StyleSheet, useWindowDimensions, View } from "react-native";
import Notification from "../../Components/Notification";
import PageTitle from "../../Components/PageTitle";
import ToothFairyButton from "../../Components/ToothFairyComponents/ToothFairyButton";
import ToothFairyModal from "../../Components/ToothFairyComponents/ToothFairyModal";
import ToothFairyOutputModal from "../../Components/ToothFairyComponents/ToothFairyOutputModal";
import ToothFairySupportButton from "../../Components/ToothFairyComponents/ToothFairySupportButton";
import useWorkspaces from "../../Hooks/useWorkspaces";
import DateFilter from "../Admin/SubComponents/Leafs/DateFilter";
import { isLargeScreen, minAppWidth } from "../MainViewContainer";
import Styles from "../Styles/Styles";
import MetaSelection from "./Components/MetaSelection";
import Constants from "../../Constants";

dayjs.extend(relativeTime);

const getColumns = (t) => [
  {
    title: t("batch.columns.id"),
    width: 0.0001,
  },
  {
    title: t("batch.columns.name"),
    width: 0.2,
  },
  {
    title: t("batch.columns.type", { defaultValue: "Type" }),
    width: 0.08,
  },
  {
    title: t("batch.columns.createdBy"),
    width: 0.13,
  },
  {
    title: t("batch.columns.lastChanged"),
    width: 0.14,
  },
  {
    title: t("batch.columns.score"),
    width: 0.1,
  },
  {
    title: t("batch.columns.duration"),
    width: 0.1,
  },
  {
    title: t("batch.columns.status"),
    width: 0.1,
  },
  {
    title: t("batch.columns.actions"),
    isAction: true,
    width: 0.05,
  },
];

const BatchScreen = ({
  onUpdateStartDate = () => {},
  onUpdateEndDate = () => {},
  handleFileSelect = () => {},
  startDate,
  endDate,
  typeFilter,
  setTypeFilter,
  batchTypes,
  selectedType,
  setSelectedType,
  handleFieldUpdate = () => {},
  handleBatchInitiation = () => {},
  handleCancelBatch = () => {},
  batchLogs,
  hasAnyData,
  batchParsingInProgress,
  handleBatchOutputDataDownload,
  batchConfig,
  setBatchConfig,
  ...props
}) => {
  const dimensions = useWindowDimensions();
  const { currentTheme } = AppTheme.useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { t } = useTranslation();
  const [isBatchInitiationModalOpen, setIsBatchInitiationModalOpen] = useState(
    false,
  );
  const [selectedItem, setSelectedItem] = useState(null);
  const styles = styleGenerator(currentTheme);
  const { state } = AppStateManager.useAppStateManager();
  const {
    getActiveWorkspace,
    getWorkspaceEntities,
    getUserName,
  } = useWorkspaces();
  const topics = getWorkspaceEntities("topic")?.map((topic) => {
    topic.name = topic?.label;
    topic.category = topic.type;
    return topic;
  });

  const typeShortBy = useMemo(() => {
    const m = {};
    (batchTypes || Constants.BATCH_TYPES)?.forEach((bt) => {
      m[bt.key] = bt.short;
    });
    return m;
  }, [batchTypes]);

  const isEmpty = !hasAnyData;

  const handleItemSelection = (item) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  //   const generateColorForItem = (item) => {
  //     if (item?.status === "swapping") return currentTheme?.theme?.orange;
  //     else if (isTrainingLogIdActive(item?.id))
  //       return currentTheme?.theme?.primary;
  //     else if (item.status !== "dispatched" && item.status !== "inProgress")
  //       return currentTheme?.theme?.grey;
  //     else return currentTheme?.theme?.red;
  //   };
  const batchInitiationModal = useMemo(() => {
    return (
      <ToothFairyModal
        transparent
        modalStyle={
          Platform.OS === "web"
            ? {
                width: 520,

                maxHeight: "80%",
                margin: "auto",
                borderRadius: 20,
                marginHorizontal: "auto",
                height: "auto",
              }
            : {}
        }
        isModalVisible={isBatchInitiationModalOpen}
        animationType="none"
        modalBackdrop={currentTheme?.theme?.softBackgroundColor}
        isCloseButtonVisible
        modalTitle={t("batch.modal.start", {
          type: Constants.mapKeysToNames(selectedType, true),
        })}
        modalInnerContentStyle={{
          paddingHorizontal: 0,
          paddingVertical: 0,
        }}
        closeModal={() => {
          setIsBatchInitiationModalOpen(false);
          setBatchConfig({
            name: "",
            description: "",
            mode: "static",
            maxTurns: 50,
            consistencyThreshold: 0.8,
          });
        }}
      >
        <MetaSelection
          batchConfig={batchConfig}
          selectedType={selectedType}
          setSelectedType={setSelectedType}
          batchTypes={batchTypes}
          setBatchConfig={setBatchConfig}
          setIsBatchInitiationModalOpen={setIsBatchInitiationModalOpen}
          topics={topics}
          handleBatchInitiation={handleBatchInitiation}
        />
      </ToothFairyModal>
    );
  }, [
    handleBatchInitiation,
    selectedType,
    state.activeWorkspaceID,
    isBatchInitiationModalOpen,
    setIsBatchInitiationModalOpen,
    state?.benchmarks?.length,
    JSON.stringify(selectedItem),
    JSON.stringify(batchConfig),
  ]);
  const batchLogsList = () => {
    return (
      <View
        style={{
          minHeight: 200,
          maxWidth: !isLargeScreen() && dimensions.width - 30,
        }}
      >
        {hasAnyData ? (
          <View
            style={{
              height: dimensions.height - 147,
              minHeight: 400,
              zIndex: -1,
            }}
          >
            <AppTable
              showPagination
              itemsPerPage={25}
              hideSearch={false}
              searchPlaceholder={t("batch.searchPlaceholder")}
              onItemDelete={(item) => {
                if (item._originalItem?.status === "dispatched") {
                  handleCancelBatch(item?.id);
                }
              }}
              customDeleteIcon={AppAssets.icons.close}
              style={{
                zIndex: -1,
                height: dimensions?.height - 265,
                paddingRight: 0,
                overflowY: "auto",
                overflowX: "hidden",
                marginTop: 10,
              }}
              actionButtons
              isItemDeletable={(item) => {
                return (
                  (item.status === "dispatched" ||
                    item.status === "inProgress") &&
                  !batchParsingInProgress
                );
              }}
              isItemEditable={(item) => {
                return item.status === "completed" || item.status === "inError";
              }}
              handleDocumentSelection={(item, index) => {
                handleItemSelection(item?.item);
              }}
              handleDocumentDeletion={(item, index) => {
                if (item.status === "dispatched") handleCancelBatch(item.id);
                else if (item.status === "inProgress")
                  handleCancelBatch(item.id);
              }}
              headers={getColumns(t)}
              items={batchLogs
                ?.sort((a, b) =>
                  a?._lastChangedAt < b?._lastChangedAt ? 1 : -1,
                )
                ?.map((item, index) => {
                  const isABM = item?.type === "abm";
                  const scoreValue = isABM
                    ? item?.batchMeta?.consistency_summary
                        ?.average_consistency_score
                    : item?.batchMeta?.scoring_summary?.average_overall_score;
                  return {
                    id: item?.id || t("batch.na"),
                    name: item?.name || t("batch.noName"),
                    type: typeShortBy[item?.type] || item?.type || " ",
                    createdBy: getUserName(item.createdBy) || " ",
                    lastChanged:
                      dayjs(item._lastChangedAt).format("DD/MM/YYYY") || " ",
                    score:
                      parseFloat(scoreValue || 0)
                        .toFixed(2)
                        ?.toString() || t("batch.na"),
                    duration: item?.batchMeta?.duration
                      ? parseFloat(
                          (item?.batchMeta?.duration || 0) / 60,
                        ).toFixed(2)
                      : t("batch.na"),
                    status:
                      item.status === "dispatched"
                        ? t("batch.status.inQueue")
                        : t(`batch.status.${item.status}`) ||
                          item.status ||
                          " ",
                    actions: " ",
                  };
                })}
            >
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  flexWrap: "wrap",
                  justifyContent: "flex-end",
                  gap: 10,
                }}
              >
                <DateFilter
                  startDate={startDate}
                  endDate={endDate}
                  onUpdateEndDate={onUpdateEndDate}
                  onUpdateStartDate={onUpdateStartDate}
                />
                <AppDropDown
                  dropDownStyle={{
                    width: 200,
                    textAlign: "center",
                    marginLeft: "auto",
                    borderColor: currentTheme?.theme?.border_color,
                    zIndex: -1,
                  }}
                  wrapperContainer={{
                    paddingBottom: 5,
                  }}
                  isElevated={false}
                  dropdownIconColor="transparent"
                  items={[
                    {
                      id: "all",
                      label: t("batch.columns.allTypes", {
                        defaultValue: "All types",
                      }),
                    },
                    ...(batchTypes || Constants.BATCH_TYPES)?.map((bt) => ({
                      id: bt.key,
                      label: `${bt.short} — ${bt.label}`,
                    })),
                  ]}
                  propertyLabel="label"
                  propertyValue="id"
                  selectedValue={typeFilter}
                  onValueChange={(itemValue) => setTypeFilter(itemValue)}
                />
              </View>
            </AppTable>
          </View>
        ) : (
          <View
            style={{
              flex: 1,
              width: "100%",

              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <AppPlaceholderView
              wrapperStyle={{
                width: "100%",
                marginHorizontal: 0,
                justifyContent: "center",
                alignItems: "center",
              }}
              size={30}
              icon={AppAssets.icons.orbit}
              mainCaption={t("batch.emptyPlaceholder")}
              subCaption={t("batchLogsubCaption")}
            />
            <ToothFairyButton
              {...Styles.minifiedButtonStyle}
              wrapperStyle={{ alignSelf: "center" }}
              isDisabled={batchParsingInProgress || !state?.isWorkspaceReady}
              onPress={() => {
                setIsBatchInitiationModalOpen(true);
              }}
              style={{
                height: Constants.appButtonHeight,
                width: 120,
                marginLeft: 0,
                marginRight: 0,
              }}
              title={t("batch.runBenchmark")}
              accessibilityLabel="batchButton"
            />
          </View>
        )}
      </View>
    );
  };

  return (
    <AppBaseScreen
      isScrollable={true}
      style={{ minHeight: 700, marginTop: 10 }}
    >
      <View style={styles.container}>
        <View style={styles.titleContainer}>
          <PageTitle>{t("batch.title")}</PageTitle>
          <ToothFairySupportButton />
          {!isEmpty && (
            <ToothFairyButton
              {...Styles.minifiedButtonStyle}
              isDisabled={batchParsingInProgress || !state?.isWorkspaceReady}
              onPress={() => {
                setIsBatchInitiationModalOpen(true);
              }}
              title={t("batch.runBenchmark")}
              accessibilityLabel="batchButton"
            />
          )}
          {batchParsingInProgress && (
            <Notification
              style={{
                position: "absolute",
                right: 0,
                top: 10,
              }}
              color={"primary"}
              dotValue={1}
              label={t("batch.benchmarkInProgress")}
              isProgress={true}
            />
          )}
        </View>

        {batchLogsList()}
        <ToothFairyOutputModal
          isEventDriven={true}
          requestBodyType="form-data"
          path={"documents/uploadBatchParsing"}
          resultObjectTitle={t("batch.modal.batchOutput")}
          responseCopyBtnTitle={t("batch.modal.copyOutput")}
          resultObjectIcon={AppAssets.icons.duplicate}
          requestPayload={{
            filename: "your_batch_file.csv",
            workspaceid: getActiveWorkspace()?.id,
            type: selectedItem?.type,
            name: selectedItem?.name,
            description: selectedItem?.description,
          }}
          handleFieldUpdate={(id, property, value) => {
            handleFieldUpdate(id, property, value);
            setSelectedItem({
              ...selectedItem,
              [property]: value,
            });
          }}
          createdBy={selectedItem?.createdBy}
          lastChangedAt={dayjs(selectedItem?._lastChangedAt).format(
            "DD/MM/YYYY",
          )}
          meta={selectedItem?.batchMeta}
          showSummary={true}
          name={selectedItem?.name}
          description={selectedItem?.description}
          urlInputWidth={550}
          isOutputUpToDate={true}
          handleOutputResize={() => {}}
          isOutputExpanded={true}
          batchParsingInProgress={batchParsingInProgress}
          parsingInProgress={false}
          batchLogId={selectedItem?.id}
          data={selectedItem?.batchMeta}
          subCaption={t("batch.modal.batchMeta")}
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          checkLength={false}
          handleDataDownload={handleBatchOutputDataDownload}
          includeConfig={true}
          configObjectTitle={t("batch.modal.batchConfig")}
          configObjectIcon={AppAssets.icons.cogWheel}
          configObject={selectedItem?.batchConfig}
        />
      </View>
      {batchInitiationModal}
    </AppBaseScreen>
  );
};

const styleGenerator = (currentTheme) => {
  return StyleSheet.create({
    container: {
      paddingHorizontal: 20,

      minWidth: minAppWidth(),
    },
    listContentContainer: {
      padding: 0,
    },
    listHeader: {
      // backgroundColor: currentTheme?.theme?.lightGrey,
    },
    listItemContainer: {
      flexDirection: "row",
      padding: 10,
    },
    paginationWrapper: {
      marginTop: 10,
      alignSelf: "flex-end",
    },
    title: {
      fontSize: currentTheme?.theme?.largest_font_size,
      fontWeight: currentTheme?.theme?.font_bold,
      marginRight: 20,
    },
    titleContainer: {
      alignItems: "center",
      flexDirection: "row",
    },
  });
};

export default BatchScreen;
